import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, canAccessVitals } from '../../utils/auth';
import { getPatients, addVital, addLog, getVitals, updateVital, addEditRequest } from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

export default function VitalsEntry() {
  const navigate = useNavigate();
  const user = getUser();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [patientVitals, setPatientVitals] = useState<VitalReading[]>([]);

  const [vitalData, setVitalData] = useState({
    systolic: '',
    diastolic: '',
    heartRate: '',
    spo2: '',
    temperature: '',
    tempUnit: 'celsius' as 'celsius' | 'fahrenheit',
    rbs: '',
    cholesterol: '',
    height: '',
    heightUnit: 'cm' as 'cm' | 'feet',
    weight: '',
    bloodGroup: '',
    colourBlindness: '',
    visualAcuity: '',
    ecg: '',
    bodyFat: '',
  });

  // Edit request modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editVitalType, setEditVitalType] = useState('');
  const [editReason, setEditReason] = useState('');
  const [editNewValue, setEditNewValue] = useState('');

  useEffect(() => {
    if (!user || !canAccessVitals(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSearch = async (value: string) => {
    setSearchTerm(value);
    if (value.trim().length > 0) {
      const patients = await getPatients();
      const results = patients.filter(
        (p) =>
          p.id.toLowerCase().includes(value.toLowerCase()) ||
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.contactNumber.includes(value)
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (${patient.id})`);
    setShowResults(false);
    
    // Load patient vitals to check what's already done
    const vitals = await getVitals();
    const patientVitalsData = vitals.filter(v => v.patientId === patient.id);
    setPatientVitals(patientVitalsData);
  };

  const canEnterVital = (vitalType: string): boolean => {
    if (!user) return false;
    if (user.role === 'master_admin' || user.role === 'admin' || user.role === 'all_vitals') return true;
    if (user.role === 'body_fat' && vitalType === 'body_fat') return true;
    return user.role === vitalType;
  };

  const isAdmin = (): boolean => {
    return user?.role === 'master_admin' || user?.role === 'admin';
  };

  const getVitalStatus = (vitalType: string): { done: boolean; value?: string; vitalId?: string } => {
    const vital = patientVitals.find(v => v.type === vitalType);
    if (vital) {
      return { done: true, value: vital.value + (vital.unit ? ` ${vital.unit}` : ''), vitalId: vital.id };
    }
    return { done: false };
  };

  const handleRequestEdit = (vitalType: string) => {
    setEditVitalType(vitalType);
    setEditNewValue('');
    setEditReason('');
    setShowEditModal(true);
  };

  const submitEditRequest = async () => {
    if (!selectedPatient || !editVitalType || !editNewValue.trim() || !editReason.trim()) {
      return;
    }

    const vitalStatus = getVitalStatus(editVitalType);
    if (!vitalStatus.done || !vitalStatus.vitalId) return;

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();

    addEditRequest({
      id: `ER${Date.now()}`,
      patientId: selectedPatient.id,
      patientName: selectedPatient.name,
      vitalId: vitalStatus.vitalId,
      vitalType: editVitalType,
      currentValue: vitalStatus.value || '',
      newValue: editNewValue,
      requestedBy: user?.username || '',
      requestedAt: timestamp,
      reason: editReason,
      status: 'pending',
    });

    addLog({
      id: Date.now().toString(),
      userId: user?.id || '',
      username: user?.username || '',
      action: 'Edit Request',
      details: `Requested ${editVitalType} update for ${selectedPatient.name} (${selectedPatient.id})`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp,
    });

    setShowEditModal(false);
    setEditVitalType('');
    setEditNewValue('');
    setEditReason('');
    alert('Edit request submitted successfully! Awaiting admin approval.');
  };

  const convertHeight = (value: string, unit: string): string => {
    if (unit === 'feet') {
      const feet = parseFloat(value);
      return (feet * 30.48).toFixed(2);
    }
    return value;
  };

  const convertTemperature = (value: string, unit: string): string => {
    if (unit === 'fahrenheit') {
      const fahrenheit = parseFloat(value);
      return ((fahrenheit - 32) * 5 / 9).toFixed(2);
    }
    return value;
  };

  // BMI Calculation
  const calculateBMI = (): { bmi: number; category: string; color: string } | null => {
    if (!vitalData.height || !vitalData.weight) return null;
    
    const heightValue = parseFloat(vitalData.height);
    const weightValue = parseFloat(vitalData.weight);
    
    if (isNaN(heightValue) || isNaN(weightValue) || heightValue <= 0 || weightValue <= 0) return null;
    
    const heightInCm = vitalData.heightUnit === 'feet' ? heightValue * 30.48 : heightValue;
    const heightInM = heightInCm / 100;
    const bmi = weightValue / (heightInM * heightInM);
    
    let category = '';
    let color = '';
    
    if (bmi < 18.5) {
      category = 'Underweight';
      color = 'text-blue-600';
    } else if (bmi >= 18.5 && bmi < 25) {
      category = 'Normal';
      color = 'text-green-600';
    } else if (bmi >= 25 && bmi < 30) {
      category = 'Overweight';
      color = 'text-orange-600';
    } else {
      category = 'Obese';
      color = 'text-red-600';
    }
    
    return { bmi: parseFloat(bmi.toFixed(1)), category, color };
  };

  const bmiData = calculateBMI();

  // Check if visual acuity test is already done for selected patient
  const hasVisualAcuityTest = (): boolean => {
    if (!selectedPatient) return false;
    return getVitalStatus('visual_acuity').done;
  };

  // For body_fat role, only show body fat section
  const isBodyFatRole = user?.role === 'body_fat';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedPatient || !user) return;

    setLoading(true);

    const { browser, device, deviceManufacturer, deviceModel } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    const timestamp = new Date().toISOString();

    const recordVital = async (type: VitalReading['type'], value: string, unit: string) => {
      if (!value.trim()) return;
      const status = getVitalStatus(type);
      if (status.done && !isAdmin()) return;
      if (status.done && isAdmin() && status.vitalId) {
        await updateVital(status.vitalId, { value, unit, recordedAt: timestamp, recordedBy: user.username });
      } else if (!status.done) {
        await addVital({
          id: `V${Date.now()}_${type}`,
          patientId: selectedPatient.id,
          type,
          value,
          unit,
          recordedAt: timestamp,
          recordedBy: user.username,
        });
      }
    };

    if (!isBodyFatRole) {
      if (vitalData.systolic && vitalData.diastolic && canEnterVital('bp')) {
        await recordVital('bp', `${vitalData.systolic}/${vitalData.diastolic}`, 'mmHg');
      }
      if (vitalData.heartRate && canEnterVital('heart_rate')) {
        await recordVital('heart_rate', vitalData.heartRate, 'bpm');
      }
      if (vitalData.spo2 && canEnterVital('spo2')) {
        await recordVital('spo2', vitalData.spo2, '%');
      }
      if (vitalData.temperature && canEnterVital('temperature')) {
        const tempVal = convertTemperature(vitalData.temperature, vitalData.tempUnit);
        await recordVital('temperature', tempVal, '°C');
      }
      if (vitalData.rbs && canEnterVital('rbs')) {
        await recordVital('rbs', vitalData.rbs, 'mg/dL');
      }
      if (vitalData.cholesterol && canEnterVital('cholesterol')) {
        await recordVital('cholesterol', vitalData.cholesterol, 'mg/dL');
      }
      if (vitalData.height && vitalData.weight && canEnterVital('height_weight')) {
        const heightCm = convertHeight(vitalData.height, vitalData.heightUnit);
        const bmiVal = bmiData ? ` | BMI: ${bmiData.bmi} (${bmiData.category})` : '';
        await recordVital('height_weight', `${heightCm} cm / ${vitalData.weight} kg${bmiVal}`, '');
      }
      if (vitalData.bloodGroup && canEnterVital('blood_grouping')) {
        await recordVital('blood_grouping', vitalData.bloodGroup, '');
      }
      if (vitalData.colourBlindness && canEnterVital('colour_blindness')) {
        await recordVital('colour_blindness', vitalData.colourBlindness, '');
      }
      if (vitalData.visualAcuity && canEnterVital('visual_acuity')) {
        await recordVital('visual_acuity', vitalData.visualAcuity, '');
      }
      if (vitalData.ecg && canEnterVital('ecg')) {
        await recordVital('ecg', vitalData.ecg, '');
      }
    }

    if (vitalData.bodyFat && canEnterVital('body_fat')) {
      await recordVital('body_fat', vitalData.bodyFat, '');
    }

    await addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Vitals Entry',
      details: `Recorded vitals for ${selectedPatient.name} (${selectedPatient.id})`,
      ipAddress,
      deviceName: device,
      browser,
      deviceManufacturer,
      deviceModel,
      timestamp,
    });

    // Reload patient vitals
    const vitals = await getVitals();
    setPatientVitals(vitals.filter(v => v.patientId === selectedPatient.id));

    setVitalData({
      systolic: '', diastolic: '', heartRate: '', spo2: '', temperature: '',
      tempUnit: 'celsius', rbs: '', cholesterol: '', height: '', heightUnit: 'cm',
      weight: '', bloodGroup: '', colourBlindness: '', visualAcuity: '', ecg: '', bodyFat: '',
    });

    setLoading(false);
    setSuccess(true);
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 cursor-pointer whitespace-nowrap mb-4 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
              <i className="ri-stethoscope-line text-teal-600 text-xl"></i>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">
                {isBodyFatRole ? 'Body Fat Analyzer Entry' : 'All Vitals Entry'}
              </h1>
              <p className="text-sm text-gray-500">
                {isBodyFatRole ? 'Search a patient and record body fat analyzer status' : 'Search a patient and record all vitals at once'}
              </p>
            </div>
          </div>
        </div>

        {success && (
          <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-base"></i>
            {isBodyFatRole ? 'Body fat status recorded successfully!' : 'Vitals recorded successfully!'}
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Search Patient <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <i className="ri-search-line text-gray-400"></i>
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="Search by Patient ID, Name, or Contact Number"
              className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
            />
          </div>

          {showResults && searchResults.length > 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {searchResults.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => selectPatient(patient)}
                  className="w-full px-4 py-3 hover:bg-teal-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer transition-colors"
                >
                  <p className="text-sm font-semibold text-gray-800">{patient.name}</p>
                  <p className="text-xs text-gray-500">
                    {patient.id} &bull; {patient.contactNumber} &bull; {patient.college}
                  </p>
                </button>
              ))}
            </div>
          )}

          {showResults && searchResults.length === 0 && (
            <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
              <p className="text-sm text-gray-500">No patients found</p>
            </div>
          )}
        </div>

        {/* Selected Patient Card */}
        {selectedPatient && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 mb-6 flex items-center gap-4">
            <div className="w-11 h-11 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
              {selectedPatient.name.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-teal-800">{selectedPatient.name}</p>
              <p className="text-xs text-teal-600 mt-0.5">
                {selectedPatient.id} &bull; {selectedPatient.age} yrs &bull; {selectedPatient.gender} &bull; {selectedPatient.college}
              </p>
            </div>
            <span className="px-2.5 py-1 bg-teal-100 text-teal-700 text-xs font-semibold rounded-full border border-teal-200 flex-shrink-0">
              {selectedPatient.bloodGroup}
            </span>
          </div>
        )}

        {/* Vitals Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isBodyFatRole && canEnterVital('bp') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-line text-red-500 text-base"></i>
                  </div>
                  Blood Pressure
                </h3>
                {selectedPatient && getVitalStatus('bp').done && (
                  <div className="flex items-center gap-2">
                    <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                      <i className="ri-checkbox-circle-fill text-xs"></i>
                      Done: {getVitalStatus('bp').value}
                    </span>
                    {!isAdmin() && (
                      <button
                        type="button"
                        onClick={() => handleRequestEdit('bp')}
                        className="px-2.5 py-1 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-full transition-colors cursor-pointer whitespace-nowrap"
                      >
                        Request Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Systolic (mmHg)</label>
                  <input
                    type="number"
                    value={vitalData.systolic}
                    onChange={(e) => setVitalData({ ...vitalData, systolic: e.target.value })}
                    disabled={!isAdmin() && getVitalStatus('bp').done}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="120"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Diastolic (mmHg)</label>
                  <input
                    type="number"
                    value={vitalData.diastolic}
                    onChange={(e) => setVitalData({ ...vitalData, diastolic: e.target.value })}
                    disabled={!isAdmin() && getVitalStatus('bp').done}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    placeholder="80"
                  />
                </div>
              </div>
            </div>
          )}

          {!isBodyFatRole && canEnterVital('heart_rate') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-line text-red-500 text-base"></i>
                  </div>
                  Heart Rate
                </h3>
                {selectedPatient && getVitalStatus('heart_rate').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('heart_rate').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Heart Rate (bpm)</label>
              <input
                type="number"
                value={vitalData.heartRate}
                onChange={(e) => setVitalData({ ...vitalData, heartRate: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                placeholder="72"
              />
            </div>
          )}

          {!isBodyFatRole && canEnterVital('spo2') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-teal-50 rounded-lg flex items-center justify-center">
                    <i className="ri-lungs-line text-teal-500 text-base"></i>
                  </div>
                  SpO2
                </h3>
                {selectedPatient && getVitalStatus('spo2').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('spo2').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Oxygen Saturation (%)</label>
              <input
                type="number"
                value={vitalData.spo2}
                onChange={(e) => setVitalData({ ...vitalData, spo2: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                placeholder="98"
                min="0"
                max="100"
              />
            </div>
          )}

          {!isBodyFatRole && canEnterVital('temperature') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-orange-50 rounded-lg flex items-center justify-center">
                    <i className="ri-temp-hot-line text-orange-500 text-base"></i>
                  </div>
                  Temperature
                </h3>
                {selectedPatient && getVitalStatus('temperature').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('temperature').value}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Temperature Value</label>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalData.temperature}
                    onChange={(e) => setVitalData({ ...vitalData, temperature: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="98.6"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Unit</label>
                  <select
                    value={vitalData.tempUnit}
                    onChange={(e) => setVitalData({ ...vitalData, tempUnit: e.target.value as 'celsius' | 'fahrenheit' })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm cursor-pointer"
                  >
                    <option value="celsius">Celsius (°C)</option>
                    <option value="fahrenheit">Fahrenheit (°F)</option>
                  </select>
                </div>
              </div>
              {vitalData.temperature && vitalData.tempUnit === 'fahrenheit' && (
                <p className="text-xs text-orange-600 mt-2 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  Will be stored as {((parseFloat(vitalData.temperature) - 32) * 5 / 9).toFixed(2)}°C
                </p>
              )}
            </div>
          )}

          {!isBodyFatRole && canEnterVital('rbs') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-rose-50 rounded-lg flex items-center justify-center">
                    <i className="ri-drop-line text-rose-500 text-base"></i>
                  </div>
                  Random Blood Sugar
                </h3>
                {selectedPatient && getVitalStatus('rbs').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('rbs').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">RBS (mg/dL)</label>
              <input
                type="number"
                value={vitalData.rbs}
                onChange={(e) => setVitalData({ ...vitalData, rbs: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                placeholder="100"
              />
            </div>
          )}

          {!isBodyFatRole && canEnterVital('cholesterol') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-green-50 rounded-lg flex items-center justify-center">
                    <i className="ri-test-tube-line text-green-500 text-base"></i>
                  </div>
                  Cholesterol
                </h3>
                {selectedPatient && getVitalStatus('cholesterol').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('cholesterol').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Cholesterol (mg/dL)</label>
              <input
                type="number"
                value={vitalData.cholesterol}
                onChange={(e) => setVitalData({ ...vitalData, cholesterol: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                placeholder="200"
              />
            </div>
          )}

          {!isBodyFatRole && canEnterVital('height_weight') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-indigo-50 rounded-lg flex items-center justify-center">
                    <i className="ri-ruler-line text-indigo-500 text-base"></i>
                  </div>
                  Height &amp; Weight
                </h3>
                {selectedPatient && getVitalStatus('height_weight').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('height_weight').value}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Height</label>
                  <input
                    type="number"
                    step="0.1"
                    value={vitalData.height}
                    onChange={(e) => setVitalData({ ...vitalData, height: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                    placeholder="170"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1.5">Unit</label>
                  <select
                    value={vitalData.heightUnit}
                    onChange={(e) => setVitalData({ ...vitalData, heightUnit: e.target.value as 'cm' | 'feet' })}
                    className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm cursor-pointer"
                  >
                    <option value="cm">Centimeters (cm)</option>
                    <option value="feet">Feet (ft)</option>
                  </select>
                </div>
              </div>
              {vitalData.height && vitalData.heightUnit === 'feet' && (
                <p className="text-xs text-indigo-600 mb-3 flex items-center gap-1">
                  <i className="ri-information-line"></i>
                  Will be stored as {(parseFloat(vitalData.height) * 30.48).toFixed(2)} cm
                </p>
              )}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1.5">Weight (kg)</label>
                <input
                  type="number"
                  step="0.1"
                  value={vitalData.weight}
                  onChange={(e) => setVitalData({ ...vitalData, weight: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                  placeholder="70"
                />
              </div>
              
              {/* BMI Display */}
              {bmiData && (
                <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-600 mb-1">Body Mass Index (BMI)</p>
                      <p className={`text-2xl font-bold ${bmiData.color}`}>
                        {bmiData.bmi}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-gray-500 mb-1">Category</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${bmiData.color} bg-white border-2`}>
                        {bmiData.category}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-indigo-200">
                    <div className="flex justify-between text-xs text-gray-500">
                      <span>&lt;18.5 Underweight</span>
                      <span>18.5-24.9 Normal</span>
                      <span>25-29.9 Overweight</span>
                      <span>≥30 Obese</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {!isBodyFatRole && canEnterVital('blood_grouping') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-red-50 rounded-lg flex items-center justify-center">
                    <i className="ri-contrast-drop-line text-red-500 text-base"></i>
                  </div>
                  Blood Grouping
                </h3>
                {selectedPatient && getVitalStatus('blood_grouping').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('blood_grouping').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-1.5">Blood Group</label>
              <select
                value={vitalData.bloodGroup}
                onChange={(e) => setVitalData({ ...vitalData, bloodGroup: e.target.value })}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm cursor-pointer"
              >
                <option value="">Select Blood Group</option>
                <option value="A+">A+</option>
                <option value="A-">A-</option>
                <option value="B+">B+</option>
                <option value="B-">B-</option>
                <option value="AB+">AB+</option>
                <option value="AB-">AB-</option>
                <option value="O+">O+</option>
                <option value="O-">O-</option>
              </select>
            </div>
          )}

          {!isBodyFatRole && canEnterVital('colour_blindness') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-violet-50 rounded-lg flex items-center justify-center">
                    <i className="ri-palette-line text-violet-500 text-base"></i>
                  </div>
                  Colour Blindness Test
                </h3>
                {selectedPatient && getVitalStatus('colour_blindness').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('colour_blindness').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Test Result</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => !hasVisualAcuityTest() && setVitalData({ ...vitalData, colourBlindness: 'No' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.colourBlindness === 'No'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  No (Normal Vision)
                </button>
                <button
                  type="button"
                  onClick={() => !hasVisualAcuityTest() && setVitalData({ ...vitalData, colourBlindness: 'Yes' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.colourBlindness === 'Yes'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Yes (Colour Blind)
                </button>
              </div>
            </div>
          )}

          {!isBodyFatRole && canEnterVital('visual_acuity') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-cyan-50 rounded-lg flex items-center justify-center">
                    <i className="ri-eye-line text-cyan-500 text-base"></i>
                  </div>
                  Visual Acuity Test
                </h3>
                {selectedPatient && getVitalStatus('visual_acuity').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('visual_acuity').value}
                  </span>
                )}
              </div>
              {hasVisualAcuityTest() && (
                <div className="mb-3 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded-lg text-xs flex items-center gap-2">
                  <i className="ri-alert-line text-sm"></i>
                  This patient has already completed the visual acuity test
                </div>
              )}
              <label className="block text-xs font-medium text-gray-600 mb-2">Test Status</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => !hasVisualAcuityTest() && setVitalData({ ...vitalData, visualAcuity: 'Completed' })}
                  disabled={hasVisualAcuityTest()}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.visualAcuity === 'Completed'
                      ? 'bg-green-500 text-white'
                      : hasVisualAcuityTest()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Completed
                </button>
                <button
                  type="button"
                  onClick={() => !hasVisualAcuityTest() && setVitalData({ ...vitalData, visualAcuity: 'Not Completed' })}
                  disabled={hasVisualAcuityTest()}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.visualAcuity === 'Not Completed'
                      ? 'bg-red-500 text-white'
                      : hasVisualAcuityTest()
                      ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Not Completed
                </button>
              </div>
            </div>
          )}

          {!isBodyFatRole && canEnterVital('ecg') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-pink-50 rounded-lg flex items-center justify-center">
                    <i className="ri-pulse-line text-pink-500 text-base"></i>
                  </div>
                  ECG Test
                </h3>
                {selectedPatient && getVitalStatus('ecg').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('ecg').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Test Status</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setVitalData({ ...vitalData, ecg: 'Yes' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.ecg === 'Yes'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setVitalData({ ...vitalData, ecg: 'No' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.ecg === 'No'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          )}

          {canEnterVital('body_fat') && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                  <div className="w-7 h-7 bg-amber-50 rounded-lg flex items-center justify-center">
                    <i className="ri-body-scan-line text-amber-500 text-base"></i>
                  </div>
                  Body Fat Analyzer
                </h3>
                {selectedPatient && getVitalStatus('body_fat').done && (
                  <span className="px-2.5 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full border border-green-200 flex items-center gap-1">
                    <i className="ri-checkbox-circle-fill text-xs"></i>
                    Done: {getVitalStatus('body_fat').value}
                  </span>
                )}
              </div>
              <label className="block text-xs font-medium text-gray-600 mb-2">Test Status</label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setVitalData({ ...vitalData, bodyFat: 'Yes' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.bodyFat === 'Yes'
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={() => setVitalData({ ...vitalData, bodyFat: 'No' })}
                  className={`flex-1 px-4 py-2.5 rounded-lg font-medium text-sm transition-all cursor-pointer whitespace-nowrap ${
                    vitalData.bodyFat === 'No'
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  No
                </button>
              </div>
            </div>
          )}

          <div className="flex gap-4 pt-2 pb-4">
            <button
              type="submit"
              disabled={loading || !selectedPatient}
              className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-semibold py-3 rounded-lg transition-colors whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Recording...
                </span>
              ) : isBodyFatRole ? 'Record Status' : 'Record Vitals'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Edit Request Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-bold text-gray-800">Request Edit</h3>
              <button
                onClick={() => setShowEditModal(false)}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer"
              >
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="mb-4">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                New Value <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={editNewValue}
                onChange={(e) => setEditNewValue(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm"
                placeholder="Enter new value"
              />
            </div>
            <div className="mb-5">
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Reason <span className="text-red-500">*</span>
              </label>
              <textarea
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 outline-none text-sm resize-none"
                rows={3}
                placeholder="Explain why this needs to be changed..."
                maxLength={300}
              />
            </div>
            <div className="flex gap-3">
              <button
                onClick={submitEditRequest}
                className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-2.5 rounded-lg transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Submit Request
              </button>
              <button
                onClick={() => setShowEditModal(false)}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}